import { useState, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Input, Slider, Button, Tooltip, Modal, Divider, Checkbox, Space, Radio } from 'antd';
import ColorPicker from '@components/ColorPicker';
import Icon from '@components/Icon';
import stores from '@stores';
import { nanoid } from '@utils/utils';

// 默认字体选项（标签将在组件内通过翻译获取）
const fontOptionsConfig = [
  { labelKey: 'textTool.fonts.defaultSans', value: 'sans-serif' },
  { labelKey: 'textTool.fonts.simSun', value: 'SimSun, serif' },
  { labelKey: 'textTool.fonts.simHei', value: 'SimHei, sans-serif' },
  { labelKey: 'textTool.fonts.microsoftYaHei', value: '"Microsoft YaHei", sans-serif' },
  { labelKey: 'textTool.fonts.kaiTi', value: 'KaiTi, serif' },
  { labelKey: 'textTool.fonts.fangSong', value: 'FangSong, serif' },
  { labelKey: 'textTool.fonts.arial', value: 'Arial, sans-serif' },
  { labelKey: 'textTool.fonts.timesNewRoman', value: '"Times New Roman", serif' },
  { labelKey: 'textTool.fonts.courierNew', value: '"Courier New", monospace' }
];

const TextTool = observer(() => {
  const { t } = useTranslation('editor');

// 默认属性
const defaultTextProperties = {
    content: t('textTool.defaultText'),
  fontSize: '24px',
  fontFamily: 'sans-serif',
  color: '#000000',
  backgroundColor: '#ffffff',
  bold: false,
  italic: false,
  underline: false,
  rotation: 0,
  scale: 1,
  x: 100,
  y: 100
};

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [selectedTextId, setSelectedTextId] = useState(null);
  const [textProperties, setTextProperties] = useState({ ...defaultTextProperties });
  
  // 监听选中的文本对象
  useEffect(() => {
    if (!stores.editor.app?.editor) return;
    
    const editor = stores.editor.app.editor;
    
    // 选中事件处理
    const onSelect = (e) => {
      const shapes = e.items || [];
      const textShape = shapes.find(shape => shape.type === 'Text');
      
      if (textShape) {
        setSelectedTextId(textShape.id);
        const storeShape = stores.editor.getShape(textShape.id);
        if (storeShape) {
          // 从存储中加载文本属性
          setTextProperties({
            id: textShape.id,
            content: storeShape.text || t('textTool.defaultText'),
            fontSize: textShape.fontSize || '24px',
            fontFamily: textShape.fontFamily || 'sans-serif',
            color: textShape.fill || '#000000',
            backgroundColor: textShape.backgroundColor || 'transparent',
            bold: textShape.bold || false,
            italic: textShape.italic || false,
            underline: textShape.underline || false,
            rotation: textShape.rotation || 0,
            scale: textShape.scale || 1,
            x: textShape.x || 100,
            y: textShape.y || 100
          });
        }
        setIsEditing(true);
      } else {
        setSelectedTextId(null);
        setIsEditing(false);
      }
    };
    
    // 取消选中事件处理
    const onUnselect = () => {
      setSelectedTextId(null);
      setIsEditing(false);
    };
    
    // 注册事件监听
    editor.on('select', onSelect);
    editor.on('unselect', onUnselect);
    
    return () => {
      // 移除事件监听
      editor.off('select', onSelect);
      editor.off('unselect', onUnselect);
    };
  }, [stores.editor.app]);
  
  // 添加新文本
  const addNewText = () => {
    if (!stores.editor.isEditing) return;
    
    // 获取画布中心位置
    const frameBox = stores.editor.app.editor.parent.children.find(child => child.tag === 'framebox');
    const centerX = frameBox ? frameBox.width / 2 : stores.option.frameConf.width / 2;
    const centerY = frameBox ? frameBox.height / 2 : stores.option.frameConf.height / 2;
    
    // 重置文本属性，但使用画布中心位置
    setTextProperties({
      ...defaultTextProperties,
      x: centerX - 50, // 减去半个默认宽度以实现居中效果
      y: centerY - 12  // 减去半个默认高度以实现居中效果
    });
    
    setIsEditing(false);
    setSelectedTextId(null);
    setIsModalOpen(true);
  };
  
  // 编辑选中的文本
  const editSelectedText = () => {
    if (!selectedTextId || !isEditing) return;
    setIsModalOpen(true);
  };
  
  // 更新文本属性
  const updateTextProperty = (property, value) => {
    setTextProperties(prev => ({
      ...prev,
      [property]: value
    }));
  };
  
  // 处理模态框确认
  const handleOk = () => {
    if (!stores.editor.isEditing) {
      setIsModalOpen(false);
      return;
    }
    
    // 计算应用样式
    const fontStyles = [];
    if (textProperties.bold) fontStyles.push('bold');
    if (textProperties.italic) fontStyles.push('italic');
    
    // 文本装饰样式
    const textDecoration = textProperties.underline ? 'underline' : 'none';
    
    // 准备文本对象属性
    const textObj = {
      type: 'Text',
      text: textProperties.content,
      fontSize: textProperties.fontSize,
      fontFamily: textProperties.fontFamily,
      fontStyle: fontStyles.join(' ') || 'normal',
      textDecoration,
      fill: textProperties.color,
      backgroundColor: textProperties.backgroundColor === 'transparent' ? undefined : textProperties.backgroundColor,
      zIndex: stores.editor.shapes.size + 1,
      rotation: textProperties.rotation,
      editable: true,
      resizable: true,
      movable: true,
      rotateable: true,
      resizeFontSize: true,
      x: textProperties.x,
      y: textProperties.y,
      lockRatio: false,
      autoSize: false,
      padding: [4, 8],
      minWidth: 20,
      minHeight: 20
    };
    
    try {
      if (isEditing && selectedTextId) {
        // 更新现有文本
        const shape = stores.editor.getShape(selectedTextId);
        if (shape) {
          // 更新存储中的文本属性
          const updatedShape = {
            ...shape,
            ...textObj
          };
          stores.editor.addShape(updatedShape);
          
          // 更新编辑器中的文本
          const editorShape = stores.editor.app.editor.get(selectedTextId);
          if (editorShape) {
            Object.assign(editorShape, textObj);
            stores.editor.app.editor.update(editorShape);
          }
        }
      } else {
        // 添加新文本
        const id = nanoid();
        const newTextObj = {
          id,
          ...textObj
        };
        
        // 添加到存储
        stores.editor.addShape(newTextObj);
        
        // 获取编辑器上下文和画布
        const frame = stores.editor.app.tree.children[0];
        
        // 使用原生LeaferUI方法添加Text元素
        if (frame) {
          // 创建新的文本对象并添加到画布
          const textElement = new Text({
            id,
            text: newTextObj.text,
            x: newTextObj.x,
            y: newTextObj.y,
            fontSize: parseInt(newTextObj.fontSize),
            fontFamily: newTextObj.fontFamily,
            fontStyle: newTextObj.fontStyle,
            textDecoration: newTextObj.textDecoration,
            fill: newTextObj.fill,
            backgroundColor: newTextObj.backgroundColor,
            zIndex: newTextObj.zIndex,
            rotation: newTextObj.rotation,
            editable: true,
            resizable: true,
            movable: true,
            rotateable: true,
            resizeFontSize: true,
            lockRatio: false,
            autoSize: false,
            padding: [4, 8],
            minWidth: 20,
            minHeight: 20
          });
          
          // 添加到画布并使其可选中/可编辑
          frame.add(textElement);
          stores.editor.app.editor.push(textElement);
        }
      }
    } catch (error) {
      console.error(t('textTool.error.addOrUpdateText'), error);
    }
    
    // 无论成功或失败，都关闭模态框
    setIsModalOpen(false);
  };
  
  // 删除文本
  const handleDelete = () => {
    if (isEditing && selectedTextId) {
      const shape = stores.editor.getShape(selectedTextId);
      if (shape) {
        stores.editor.removeShape(shape);
        stores.editor.app.editor.remove(selectedTextId);
      }
    }
    setIsModalOpen(false);
  };
  
  // 处理模态框取消
  const handleCancel = () => {
    setIsModalOpen(false);
  };
  
  return (
    <>
      <Button
        type="text"
        className="w-full flex items-center justify-start rounded-md px-2 py-1 h-auto hover:bg-purple-50 dark:hover:bg-gray-800 hover:text-purple-600 dark:hover:text-purple-400"
        onClick={isEditing ? editSelectedText : addNewText}
      >
        <Icon.Type size={18} useFa={true} className="mr-2" />
        <span className="text-xs">{isEditing ? t('textTool.editText') : t('textTool.addText')}</span>
      </Button>
      
      <Modal
        title={
          <div className="flex items-center text-purple-600 dark:text-purple-400">
            <Icon.Type size={20} useFa={true} className="mr-2" />
            <span>{isEditing ? t('textTool.editText') : t('textTool.addText')}</span>
          </div>
        }
        open={isModalOpen}
        onOk={handleOk}
        onCancel={handleCancel}
        destroyOnClose={true}
        afterClose={() => setTextProperties({ ...defaultTextProperties })}
        okText={t('textTool.okText')}
        cancelText={t('textTool.cancelText')}
        width={520}
        className="text-modal"
        okButtonProps={{ 
          className: "bg-purple-600 hover:bg-purple-700 border-purple-600 hover:border-purple-700" 
        }}
        cancelButtonProps={{
          className: "hover:text-purple-600 hover:border-purple-600"
        }}
        maskStyle={{ backdropFilter: "blur(4px)" }}
        bodyStyle={{ padding: "16px", background: stores.editor.isDark ? "#1f2937" : "#f8fafc" }}
      >
        <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
          <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
            <div className="flex items-center">
              <Icon.Type size={12} useFa={true} className="mr-1 text-purple-500 dark:text-purple-400" />
              <span>{t('textTool.textContent')}</span>
            </div>
          </Divider>
          
          <Input.TextArea
            value={textProperties.content}
            onChange={(e) => updateTextProperty('content', e.target.value)}
            placeholder={t('textTool.textContentPlaceholder')}
            rows={3}
            className="w-full mb-4"
          />
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center mb-2">
                <Icon.Palette size={12} useFa={true} className="mr-1 text-purple-500 dark:text-purple-400" />
                <span className="text-xs">{t('textTool.textColor')}</span>
              </div>
              <ColorPicker
                size='small'
                showText
                value={textProperties.color}
                onChange={(e) => updateTextProperty('color', e.toHexString())}
                className="w-full"
                presets={[
                  {
                    label: t('textTool.recommendedColors'),
                    colors: [
                      '#000000',
                      '#ffffff',
                      '#ff0000',
                      '#00ff00',
                      '#0000ff',
                      '#ffff00',
                      '#ff00ff',
                      '#00ffff',
                      '#ff9900',
                      '#9900ff',
                    ],
                  },
                ]}
              />
            </div>
            
            <div>
              <div className="flex items-center mb-2">
                <Icon.Box size={12} useFa={true} className="mr-1 text-purple-500 dark:text-purple-400" />
                <span className="text-xs">{t('textTool.backgroundColor')}</span>
              </div>
              <div>
                <ColorPicker
                  size='small'
                  showText
                  value={textProperties.backgroundColor === 'transparent' ? '#ffffff' : textProperties.backgroundColor}
                  onChange={(e) => {
                    // 直接设置背景色，无需额外逻辑
                    updateTextProperty('backgroundColor', e.toHexString());
                  }}
                  className="w-full"
                  disabled={textProperties.backgroundColor === 'transparent'}
                  presets={[
                    {
                      label: t('textTool.recommendedColors'),
                      colors: [
                        '#ffffff',
                        '#f2f2f2',
                        '#ffdddd',
                        '#ddffdd',
                        '#ddddff',
                        '#ffffdd',
                        '#ffddff',
                        '#ddffff',
                        '#ffeedd',
                        '#eeddff',
                      ],
                    },
                  ]}
                />
                <Checkbox 
                  checked={textProperties.backgroundColor === 'transparent'} 
                  onChange={(e) => {
                    updateTextProperty('backgroundColor', e.target.checked ? 'transparent' : '#ffffff');
                  }}
                  className="mt-2"
                >
                  <span className="text-xs">{t('textTool.transparentBackground')}</span>
                </Checkbox>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
          <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
            <div className="flex items-center">
              <Icon.Edit3 size={12} useFa={true} className="mr-1 text-purple-500 dark:text-purple-400" />
              <span>{t('textTool.fontSettings')}</span>
            </div>
          </Divider>
          
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <span className="text-xs">{t('textTool.font')}</span>
            </div>
            <Radio.Group 
              value={textProperties.fontFamily} 
              onChange={(e) => updateTextProperty('fontFamily', e.target.value)}
              className="w-full"
            >
              <Space direction="vertical" className="w-full">
                {fontOptionsConfig.map(font => (
                  <Radio key={font.value} value={font.value} className="w-full">
                    <span style={{ fontFamily: font.value }}>{t(font.labelKey)}</span>
                  </Radio>
                ))}
              </Space>
            </Radio.Group>
          </div>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs">{t('textTool.fontSize')}</span>
              <span className="text-xs text-gray-500">{parseInt(textProperties.fontSize)}px</span>
            </div>
            <Slider
              min={12}
              max={72}
              value={parseInt(textProperties.fontSize)}
              onChange={(value) => updateTextProperty('fontSize', `${value}px`)}
            />
          </div>
          
          <div className="mb-4">
            <div className="flex items-center mb-2">
              <span className="text-xs">{t('textTool.fontStyle')}</span>
            </div>
            <Space>
              <Checkbox checked={textProperties.bold} onChange={(e) => updateTextProperty('bold', e.target.checked)}>
                <span className="text-xs font-bold">{t('textTool.bold')}</span>
              </Checkbox>
              <Checkbox checked={textProperties.italic} onChange={(e) => updateTextProperty('italic', e.target.checked)}>
                <span className="text-xs italic">{t('textTool.italic')}</span>
              </Checkbox>
              <Checkbox checked={textProperties.underline} onChange={(e) => updateTextProperty('underline', e.target.checked)}>
                <span className="text-xs underline">{t('textTool.underline')}</span>
              </Checkbox>
            </Space>
          </div>
        </div>
        
        <div className="mb-4 bg-white dark:bg-gray-800 p-4 rounded-lg shadow-sm">
          <Divider orientation="left" orientationMargin="0" className="text-xs text-gray-500 dark:text-gray-400 my-1">
            <div className="flex items-center">
              <Icon.RotateCcw size={12} useFa={true} className="mr-1 text-purple-500 dark:text-purple-400" />
              <span>{t('textTool.transformSettings')}</span>
            </div>
          </Divider>
          
          <div className="mb-4">
            <div className="flex items-center justify-between mb-2">
              <span className="text-xs">{t('textTool.rotation')}</span>
              <span className="text-xs text-gray-500">{textProperties.rotation}°</span>
            </div>
            <Slider
              min={-180}
              max={180}
              value={textProperties.rotation}
              onChange={(value) => updateTextProperty('rotation', value)}
            />
          </div>
          
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="flex items-center mb-2">
                <span className="text-xs">{t('textTool.xPosition')}</span>
              </div>
              <Input
                type="number"
                value={textProperties.x}
                onChange={(e) => updateTextProperty('x', parseInt(e.target.value) || 0)}
                suffix="px"
              />
            </div>
            <div>
              <div className="flex items-center mb-2">
                <span className="text-xs">{t('textTool.yPosition')}</span>
              </div>
              <Input
                type="number"
                value={textProperties.y}
                onChange={(e) => updateTextProperty('y', parseInt(e.target.value) || 0)}
                suffix="px"
              />
            </div>
          </div>
        </div>
        
        {isEditing && (
          <Button 
            danger 
            className="w-full mt-4"
            onClick={handleDelete}
          >
            <div className="flex items-center justify-center">
              <Icon.Trash2 size={14} useFa={true} className="mr-1" />
              <span>{t('textTool.deleteText')}</span>
            </div>
          </Button>
        )}
      </Modal>
    </>
  );
});

export default TextTool; 